import { Box, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { ENDORSEMENT_CATEGORIES } from "../hooks/useEndorsements";
import { hasFieldSchema } from "./endorsementFieldSchemas";
import { getShortDescription } from "./endorsementDescriptions";
import type { EndorsementType, EndorsementListItem } from "@/types";

interface EndorsementTypePickerProps {
  /** Called when user selects an endorsement type */
  onTypeSelected: (type: EndorsementType) => void;
  /** Current endorsements on this policy — used to filter out already-configured types */
  endorsements: EndorsementListItem[];
}

/**
 * Categorized type selection list for the endorsement create flow.
 * Types are grouped by ENDORSEMENT_CATEGORIES. Types without field schemas
 * are shown as disabled with "(Coming soon)".
 *
 * Each policy can have at most ONE endorsement per normalized type. Types that
 * already have an active endorsement are hidden from the picker to keep the
 * list focused on what can actually be added. Inactive types remain available.
 *
 * Categories with no remaining available types are also hidden.
 *
 * Rendered as the first step inside the EndorsementCreatePanel.
 */
export const EndorsementTypePicker = ({ onTypeSelected, endorsements }: EndorsementTypePickerProps) => {
  const { theme } = useAppTheme();

  // Build a set of types that are actively configured on this policy (1:1 per type).
  // Only active endorsements are excluded — inactive ones can still be added/reconfigured.
  const activeTypes = new Set<EndorsementType>(endorsements.filter((e) => e.is_currently_active).map((e) => e.type));

  // Filter categories to only include types not actively configured, and skip empty categories
  const availableCategories = ENDORSEMENT_CATEGORIES.map((category) => ({
    ...category,
    types: category.types.filter((type) => !activeTypes.has(type)),
  })).filter((category) => category.types.length > 0);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Select the type of endorsement to add to this policy.
      </Typography>

      {availableCategories.map((category) => (
        <Box key={category.key} sx={{ mb: 2.5 }}>
          {/* Category header */}
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            sx={{
              fontSize: "0.8125rem",
              mb: 1,
              pl: 0.5,
            }}
          >
            {category.label}
          </Typography>

          {/* Type list */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {category.types.map((type, index) => {
              const hasSchema = hasFieldSchema(type);
              const isLast = index === category.types.length - 1;

              return (
                <Box
                  key={type}
                  onClick={hasSchema ? () => onTypeSelected(type) : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    cursor: hasSchema ? "pointer" : "default",
                    opacity: hasSchema ? 1 : 0.5,
                    borderBottom: isLast ? "none" : `1px solid ${theme.palette.divider}`,
                    transition: "background-color 0.15s",
                    "&:hover": hasSchema ? { bgcolor: "action.hover" } : {},
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" fontWeight={500} color={hasSchema ? "text.primary" : "text.disabled"}>
                        {formatToTitleCase(type)}
                      </Typography>
                      {!hasSchema && (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                          Coming soon
                        </Typography>
                      )}
                    </Box>
                    {hasSchema && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          lineHeight: 1.4,
                        }}
                      >
                        {getShortDescription(type)}
                      </Typography>
                    )}
                  </Box>
                  {hasSchema && <ChevronRight sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0, ml: 1 }} />}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
